import { BulkManagementTablePrimitives } from './components/BulkManagementTablePrimitives';
import React, { useCallback, useMemo } from 'react';
import { type AnalysisSubject, withSuspense } from 'venn-utils';
import { useBulkManagementTable } from './hooks/useBulkManagementTable';
import { usePortfolioManagementColumns } from './hooks/usePortfolioManagementColumns';
import { BulkManagementLoadingState } from './BulkManagementLoadingState';
import { useInvestmentFilter } from './hooks/useInvestmentFilter';
import { getBulkManagementData } from './data';

type Props = {
  subject: AnalysisSubject;
  onFundUpdated?: (fundId: string) => void;
  canEditProxies: boolean;
  showLiquidatedInvestmentsToggle: boolean;
};

export const BulkPortfolioManagementTable = withSuspense(
  BulkManagementLoadingState,
  function BulkPortfolioManagementTable({
    subject,
    onFundUpdated,
    canEditProxies,
    showLiquidatedInvestmentsToggle,
  }: Props) {
    const {
      bulkManagementTableModals,
      openedSecondaryId,
      uniqFunds,
      selectedFunds,
      onProxyChange,
      rangeAnalysis,
      proxyDataByFund,
      holdingsProxyDataByFund,
      rangeDataByFund,
      benchmarkRange,
      onClearSelected,
      gridRef,
      onSelectionChanged,
      groupHeaderHeight,
      rowClassRules,
      selectedAllocationDate,
      allocationDates,
      setOpenedSecondaryId,
      setSelectedFundIds,
      showLiquidatedInvestments,
      benchmarkInfo,
      rangeData,
      primaryFactorLens,
      setShowLiquidatedInvestments,
      cashRateRange,
      referenceRateRange,
    } = useBulkManagementTable({
      subject,
      shouldHighlightRowsWithHistoricalErrors: true,
    });
    const { rowData, pinnedRowData } = useMemo(
      () =>
        getBulkManagementData(
          subject,
          uniqFunds,
          proxyDataByFund,
          holdingsProxyDataByFund,
          rangeDataByFund,
          benchmarkRange,
          benchmarkInfo.benchmark,
          rangeData,
          primaryFactorLens,
          cashRateRange,
          referenceRateRange,
          allocationDates,
          false,
        ),
      [
        subject,
        uniqFunds,
        proxyDataByFund,
        holdingsProxyDataByFund,
        rangeDataByFund,
        benchmarkRange,
        benchmarkInfo.benchmark,
        rangeData,
        primaryFactorLens,
        cashRateRange,
        referenceRateRange,
        allocationDates,
      ],
    );

    const onReturnsProxyPickerOpen = useCallback(
      () => bulkManagementTableModals.open('returnsProxy'),
      [bulkManagementTableModals],
    );
    const onHoldingsProxyPickerOpen = useCallback(
      () => bulkManagementTableModals.open('holdingsProxy'),
      [bulkManagementTableModals],
    );
    const { columnDefs, autoGroupColumnDef } = usePortfolioManagementColumns({
      subject,
      onFundUpdated,
      setSelectedFundIds,
      setOpenedSecondaryId,
      canEditProxies,
      onReturnsProxyPickerOpen,
      onHoldingsProxyPickerOpen,
      rowData,
      showLiquidatedInvestmentsToggle,
      showLiquidatedInvestments,
      setShowLiquidatedInvestments,
    });

    const { isExternalFilterPresent, doesExternalFilterPass } = useInvestmentFilter({
      portfolio: subject?.portfolio,
      selectedAllocationDate,
      allocationDates,
      gridApi: gridRef.current?.api,
      showAllocations: false,
      showLiquidatedInvestments,
      showLiquidatedInvestmentsToggle,
    });

    return (
      <BulkManagementTablePrimitives.Root
        openedSecondaryId={openedSecondaryId}
        uniqFunds={uniqFunds}
        selectedFunds={selectedFunds}
        onProxyChange={onProxyChange}
        disableSelection={!canEditProxies}
        rangeAnalysis={rangeAnalysis}
        proxyDataByFund={proxyDataByFund}
        holdingsProxyDataByFund={holdingsProxyDataByFund}
        rangeDataByFund={rangeDataByFund}
        onClearSelected={onClearSelected}
        modals={bulkManagementTableModals}
      >
        <BulkManagementTablePrimitives.Grid
          gridRef={gridRef}
          rowData={rowData}
          pinnedRowData={pinnedRowData}
          columnDefs={columnDefs}
          onSelectionChanged={onSelectionChanged}
          groupHeaderHeight={groupHeaderHeight}
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={doesExternalFilterPass}
          disableSelection={!canEditProxies}
          autoGroupColumnDef={autoGroupColumnDef}
          rowClassRules={rowClassRules}
          hasSelections={canEditProxies && selectedFunds.length > 0}
        />
      </BulkManagementTablePrimitives.Root>
    );
  },
);
